/* eslint-disable @next/next/no-img-element */
import { Skeleton } from '@mui/material';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { PATH_NAME } from '../../../../configs/pathName';
import { GROUP_ROLE_ACCESS } from '../../../../constant/QueryKeyName';
import {
  DEFAULT_GROUP_COVER,
  DoccenIconNoColor,
} from '../../../../constant/imgImport';
import { ROLE_ACCESS } from '../../../../constant/storage';
import useRequestAccessGroup from '../../../../hooks/my-group/useRequestAccessGroup';
import useToast from '../../../../hooks/useToast';
import { IBusinessProfileGroup } from '../../../../interfaces/profile.interface';
import { FolderStatusEnum } from '../../../../interfaces/training.interface';
import { CoverWrapper } from '../../../image/Cover';
import {
  FilledGlobeIcon,
  FilledLockIcon,
  FilledProfileTwoUsersIcon,
} from '../../svgIcon/filled';
import BusinessJoinGroupButton, {
  EBusinessGroupButtonMode,
} from './BusinessJoinGroupButton';

const BusinessGroupPreview = ({
  name: groupName,
  status: groupStatus,
  numOfMembers: numberOfMember,
  id: groupId,
  currentUserPermission: role,
  isOwnerProfile,
  cover,
  avatar,
}: IBusinessProfileGroup) => {
  const [buttonMode, setButtonMode] = useState(modeMapping(role));

  const router = useRouter();
  const queryClient = useQueryClient();
  const toast = useToast();

  const {
    mutationReturn: { mutate, isLoading },
  } = useRequestAccessGroup({
    isRequested: buttonMode === EBusinessGroupButtonMode.Requested,
    opt: {
      onSuccess() {
        if (groupStatus === FolderStatusEnum.Public) {
          toast(`Tham gia nhóm ${groupName} thành công.`, 'success');
          setButtonMode(EBusinessGroupButtonMode.Joined);
          return;
        }

        toast(
          `Bạn đã ${
            buttonMode === EBusinessGroupButtonMode.Requested ? 'hủy' : 'gửi'
          } yêu cầu tham gia nhóm ${groupName}`,
          'success'
        );

        setButtonMode((prev) =>
          prev === EBusinessGroupButtonMode.Requested
            ? EBusinessGroupButtonMode.Revoked
            : EBusinessGroupButtonMode.Requested
        );
        queryClient.refetchQueries([GROUP_ROLE_ACCESS, groupId]);
      },
      onError() {
        toast('Có lỗi xảy ra.', 'error');
        setButtonMode(EBusinessGroupButtonMode.Revoked);
      },
      retry: 3,
    },
  });

  const renderStatusIcon = (
    status: FolderStatusEnum | undefined
  ): { icon: React.ReactElement; label: string } => {
    if (status === FolderStatusEnum.Private)
      return {
        icon: <FilledLockIcon width={16} height={16} />,
        label: 'Nhóm riêng tư',
      };
    return {
      icon: <FilledGlobeIcon width={16} height={16} />,
      label: 'Nhóm công khai',
    };
  };

  const handleLogicAccessGroup = async () => {
    mutate({ groupId, groupStatus: groupStatus });
  };

  return (
    <div
      onClick={() =>
        router.push({
          pathname: PATH_NAME.MY_GROUPS_DETAIL,
          query: { gid: groupId },
        })
      }
      className="flex h-full w-full max-w-full cursor-pointer flex-col gap-3 rounded-[10px] shadow-[0px_10px_20px_rgba(0,0,0,0.1)] lg:max-w-[220px]"
    >
      <div className="relative w-full pb-6">
        <CoverWrapper src={cover || DEFAULT_GROUP_COVER}></CoverWrapper>
        <div className="absolute bottom-0 left-2 h-fit drop-shadow-[0px_8px_20px_rgba(0,0,0,0.15)]">
          <img
            src={avatar || DoccenIconNoColor}
            alt="default group avatar"
            className="h-16 w-16 overflow-hidden rounded-full object-cover"
          />
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-between gap-4 px-3 pb-3">
        <div className="flex flex-1 flex-col justify-between gap-2 px-2">
          <p className="text-Text/Lightbackground/Primary line-clamp-2 text-base font-medium leading-[140%] tracking-[-0.4px]">
            {groupName}
          </p>
          <div className="flex flex-col gap-1">
            <div className="text-Text/Lightbackground/Secondary flex items-center justify-start gap-2 text-xs font-medium leading-[140%]">
              {renderStatusIcon(groupStatus).icon}
              <p>{renderStatusIcon(groupStatus).label}</p>
            </div>
            <div className="text-Text/Lightbackground/Secondary flex items-center justify-start gap-2 text-xs font-medium leading-[140%]">
              <FilledProfileTwoUsersIcon width={16} height={16} />
              <p>{numberOfMember} thành viên</p>
            </div>
          </div>
        </div>

        {!isOwnerProfile ? (
          <div className="mt-1">
            <BusinessJoinGroupButton
              handleLogicAccessGroup={handleLogicAccessGroup}
              groupId={groupId}
              mode={buttonMode}
              isLoading={isLoading}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

function modeMapping(role: ROLE_ACCESS) {
  switch (role) {
    case ROLE_ACCESS.Revoked:
      return EBusinessGroupButtonMode.Revoked;
    case ROLE_ACCESS.Requested:
      return EBusinessGroupButtonMode.Requested;
    default:
      return EBusinessGroupButtonMode.Joined;
  }
}

export const BusinessGroupPreviewSkeleton = () => {
  return (
    <Skeleton
      variant="rounded"
      width={'100%'}
      height={'100%'}
      sx={{
        maxWidth: {
          sm: 'none',
          xs: 'none',
        },
        minHeight: 200,
        borderRadius: '10px',
      }}
    ></Skeleton>
  );
};

export default BusinessGroupPreview;
